import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    IconButton,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { crearSiniestro, actualizarSiniestro } from '../../services/art/siniestroService';
import { getLesiones } from '../../services/art/lesionService';
import { listarMedicos } from '../../services/medicoService';

const FORM_INICIAL = {
    nroSiniestro: '',
    nroExpedienteSRT: '',
    comentario: '',
    tipoContingencia: 'AT',
    respuestaART: '',
    idsLesiones: [],
    idsMedicos: [],
};

// Normaliza el id que expone el catálogo de lesiones (puede venir como idLesionFisica, idLesion o id).
const obtenerIdLesion = (l) => l?.idLesionFisica ?? l?.idLesion ?? l?.id;

const TIPOS_CONTINGENCIA = [
    { code: 'AT', label: 'Accidente de Trabajo' },
    { code: 'EP', label: 'Enfermedad Profesional' },
    { code: 'EPNL', label: 'Enfermedad Profesional No Listada' },
];

export default function ModalFormularioSiniestro({ isOpen, onClose, idTramite, siniestroAEditar, onSave }) {
    const [form, setForm] = useState(FORM_INICIAL);
    const [lesiones, setLesiones] = useState([]);
    const [medicos, setMedicos] = useState([]);
    const [cargandoDatos, setCargandoDatos] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');

    const esEdicion = !!siniestroAEditar;

    // Cargar lesiones y médicos al abrir el modal
    useEffect(() => {
        if (!isOpen) return;
        const cargar = async () => {
            setCargandoDatos(true);
            try {
                const [lesionesData, medicosData] = await Promise.all([
                    getLesiones(),
                    listarMedicos(true), // solo activos
                ]);
                setLesiones(lesionesData || []);
                setMedicos(medicosData || []);
            } catch (err) {
                console.error('Error cargando datos para siniestro:', err);
            } finally {
                setCargandoDatos(false);
            }
        };
        cargar();
    }, [isOpen]);

    // Poblar formulario si es edición
    useEffect(() => {
        if (!isOpen) return;
        if (siniestroAEditar) {
            // Preferimos idsLesiones (nuevo). Caemos al idLesion legacy si el backend
            // todavía devuelve solo el campo viejo.
            const lesionesIniciales = Array.isArray(siniestroAEditar.idsLesiones) && siniestroAEditar.idsLesiones.length > 0
                ? siniestroAEditar.idsLesiones.map(Number)
                : (siniestroAEditar.idLesion != null ? [Number(siniestroAEditar.idLesion)] : []);

            setForm({
                nroSiniestro: siniestroAEditar.nroSiniestro || '',
                nroExpedienteSRT: siniestroAEditar.nroExpedienteSRT || '',
                comentario: siniestroAEditar.comentario || '',
                tipoContingencia: siniestroAEditar.tipoContingencia || '',
                respuestaART: siniestroAEditar.respuestaART || '',
                idsLesiones: lesionesIniciales,
                idsMedicos: siniestroAEditar.idsMedicos || [],
            });
        } else {
            setForm(FORM_INICIAL);
        }
        setError('');
    }, [isOpen, siniestroAEditar]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleMedicosChange = (e) => {
        const value = e.target.value;
        const selected = Array.isArray(value) ? value.map(Number) : [];
        setForm(prev => ({ ...prev, idsMedicos: selected }));
    };

    const handleLesionesChange = (e) => {
        const value = e.target.value;
        const selected = Array.isArray(value) ? value.map(Number) : [];
        setForm(prev => ({ ...prev, idsLesiones: selected }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nroSiniestro.trim()) {
            setError('El número de siniestro es obligatorio.');
            return;
        }
        setError('');
        setGuardando(true);
        try {
            const payload = {
                ...form,
                idTramite,
                idsLesiones: form.idsLesiones.length > 0 ? form.idsLesiones : [],
                idsMedicos: form.idsMedicos.length > 0 ? form.idsMedicos : null,
            };

            if (esEdicion) {
                await actualizarSiniestro(siniestroAEditar.idSiniestro, payload);
            } else {
                await crearSiniestro(payload);
            }

            if (onSave) onSave();
            onClose();
        } catch (err) {
            console.error('Error al guardar siniestro:', err);
            setError(err.response?.data?.message || err.response?.data || 'Error al guardar el siniestro.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                fontWeight: 'bold',
                textAlign: 'center'
            }}>
                {esEdicion ? 'Editar Siniestro' : 'Registrar Siniestro'}
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
                    aria-label="Cerrar"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ mt: 2, pb: 1 }}>
                {cargandoDatos ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Cargando datos...
                        </Typography>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit} id="form-siniestro">
                        {error && (
                            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                                {error}
                            </Typography>
                        )}

                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Nro. Siniestro"
                                name="nroSiniestro"
                                value={form.nroSiniestro}
                                onChange={handleChange}
                                placeholder="Ej: SIN-2026-00123"
                                required
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Nro. Expediente SRT"
                                name="nroExpedienteSRT"
                                value={form.nroExpedienteSRT}
                                onChange={handleChange}
                                placeholder="Ej: EXP-SRT-456"
                            />
                        </Box>

                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="tipo-contingencia-label">Tipo de Contingencia</InputLabel>
                                <Select
                                    labelId="tipo-contingencia-label"
                                    name="tipoContingencia"
                                    value={form.tipoContingencia}
                                    label="Tipo de Contingencia"
                                    onChange={handleChange}
                                >
                                    <MenuItem value="AT">Seleccionar...</MenuItem>
                                    {TIPOS_CONTINGENCIA.map(t => (
                                        <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Respuesta ART"
                                name="respuestaART"
                                value={form.respuestaART}
                                onChange={handleChange}
                                placeholder="Respuesta de la aseguradora"
                            />
                        </Box>

                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="lesion-label">Lesiones</InputLabel>
                                <Select
                                    labelId="lesion-label"
                                    multiple
                                    name="idsLesiones"
                                    value={form.idsLesiones}
                                    label="Lesiones"
                                    onChange={handleLesionesChange}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((id) => {
                                                const l = lesiones.find(x => obtenerIdLesion(x) === id);
                                                const nombre = l?.lesion || l?.nombre || l?.descripcion || id;
                                                return <Chip key={id} label={nombre} size="small" />;
                                            })}
                                        </Box>
                                    )}
                                >
                                    {lesiones.map(l => (
                                        <MenuItem key={obtenerIdLesion(l)} value={obtenerIdLesion(l)}>
                                            {l.lesion || l.nombre || l.descripcion || 'Sin nombre'}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="medicos-label">Médicos asignados</InputLabel>
                                <Select
                                    labelId="medicos-label"
                                    multiple
                                    name="idsMedicos"
                                    value={form.idsMedicos}
                                    label="Médicos asignados"
                                    onChange={handleMedicosChange}
                                >
                                    {medicos.map(m => (
                                        <MenuItem key={m.idMedico} value={m.idMedico}>
                                            {m.nombre} {m.apellido}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <TextField
                            fullWidth
                            margin="normal"
                            label="Comentario"
                            name="comentario"
                            value={form.comentario}
                            onChange={handleChange}
                            placeholder="Observaciones adicionales sobre el siniestro..."
                            multiline
                            rows={3}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={guardando}
                    sx={{ borderRadius: '20px' }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    type="submit"
                    form="form-siniestro"
                    disabled={guardando || cargandoDatos}
                    sx={{ borderRadius: '20px' }}
                >
                    {guardando ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={18} color="inherit" />
                            Guardando...
                        </Box>
                    ) : (esEdicion ? 'Guardar Cambios' : 'Registrar Siniestro')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
